import React from 'react'
import * as PhosphorIcons from '@phosphor-icons/react'

const ContractAnalysisViewResult = ({result}) => {
  const getSeverityColor = (severity) => {
    switch(severity?.toLowerCase()) {
      case 'high':
        return 'bg-error/20 text-error';
      case 'medium':
        return 'bg-warning/20 text-warning';
      case 'low':
        return 'bg-success/20 text-success';
      default:
        return 'bg-base-200 text-base-content';
    }
  };

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Parties Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <PhosphorIcons.Users size={20} weight="bold" className="text-primary"/>
                </div>
                <h3 className="card-title text-lg">Contract Parties</h3>
              </div>

              <div className="space-y-3">
                {Array.isArray(result?.contract_analysis?.parties) && result.contract_analysis.parties.map((party, index) => (
                  <div key={index} className="p-3 bg-base-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="font-medium capitalize">{party?.name}</div>
                      <span className="text-xs px-2 py-0.5 bg-primary/10 rounded-full capitalize">{party?.type}</span>
                    </div>
                    <div className="text-sm mt-1">
                      <span className="text-base-content/70">Role: </span>
                      <span className="capitalize">{party?.role}</span>
                    </div>
                    {Array.isArray(party?.obligations) && party.obligations.length > 0 && (
                      <div className="mt-2">
                        <span className="text-sm text-base-content/70">Obligations:</span>
                        <div className="mt-1 space-y-1">
                          {party.obligations.map((obligation, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm">
                              <PhosphorIcons.CaretRight size={16} className="text-primary"/>
                              <span className="capitalize">{obligation.replace(/_/g, ' ')}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {Array.isArray(party?.rights) && party.rights.length > 0 && (
                      <div className="mt-2">
                        <span className="text-sm text-base-content/70">Rights:</span>
                        <div className="mt-1 space-y-1">
                          {party.rights.map((right, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm">
                              <PhosphorIcons.CaretRight size={16} className="text-secondary"/>
                              <span className="capitalize">{right.replace(/_/g, ' ')}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {party?.contact_details && Object.values(party?.contact_details).some(v => v) && (
                      <div className="mt-2 text-sm border-t border-base-300 pt-2">
                        {party?.contact_details?.representative && (
                          <div><span className="text-base-content/70">Representative: </span>{party.contact_details.representative}</div>
                        )}
                        {party?.contact_details?.email && (
                          <div><span className="text-base-content/70">Email: </span>{party?.contact_details?.email}</div>
                        )}
                        {party?.contact_details?.phone && (
                          <div><span className="text-base-content/70">Phone: </span>{party?.contact_details?.phone}</div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Key Terms & Financial Terms Cards */}
          <div className="space-y-4">
            {/* Key Terms */}
            <div className="card bg-base-100 shadow-sm">
              <div className="card-body p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-2 rounded-lg bg-secondary/10">
                    <PhosphorIcons.ListChecks size={20} weight="bold" className="text-secondary"/>
                  </div>
                  <h3 className="card-title text-lg">Key Terms</h3>
                </div>

                <div className="space-y-3">
                  {Array.isArray(result?.contract_analysis?.key_terms) && result.contract_analysis.key_terms.map((term, index) => (
                    <div key={index} className="p-3 bg-base-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="font-medium capitalize">{term?.type?.replace(/_/g, ' ')}</div>
                        {term?.is_critical && (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-error/20 text-error">Critical</span>
                        )}
                      </div>
                      <div className="text-sm mt-1">
                        <div><span className="text-base-content/70">Value: </span>{term?.value}</div>
                        {term?.currency_amount && (
                          <div className="mt-1">
                            <span className="text-base-content/70">Amount: </span>
                            {term?.currency_amount?.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                          </div>
                        )}
                        {term?.frequency && (
                          <div className="mt-1"><span className="text-base-content/70">Frequency: </span>{term?.frequency}</div>
                        )}
                        {term?.details && (
                          <div className="mt-1"><span className="text-base-content/70">Details: </span>{term?.details}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Financial Terms */}
            {result?.contract_analysis?.financial_terms && (
              <div className="card bg-base-100 shadow-sm">
                <div className="card-body p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-2 rounded-lg bg-success/10">
                      <PhosphorIcons.CurrencyDollar size={20} weight="bold" className="text-success"/>
                    </div>
                    <h3 className="card-title text-lg">Financial Terms</h3>
                  </div>

                  <div className="space-y-3">
                    {Array.isArray(result?.contract_analysis?.financial_terms) && result.contract_analysis.financial_terms.map((term, index) => (
                      <div key={index} className="p-3 bg-base-200 rounded-lg">
                        <div className="font-medium capitalize">{term?.type?.replace(/_/g, ' ')}</div>
                        <div className="text-sm mt-1">
                          {term?.amount && (
                            <div>
                              <span className="text-base-content/70">Amount: </span>
                              {term?.amount?.toLocaleString('en-US', { style: 'currency', currency: term?.currency || 'USD' })}
                            </div>
                          )}
                          {term?.payment_schedule && (
                            <div className="mt-1">
                              <span className="text-base-content/70">Schedule: </span>{term?.payment_schedule}
                            </div>
                          )}
                          {Array.isArray(term?.conditions) && term.conditions.length > 0 && (
                            <div className="mt-2">
                              <span className="text-base-content/70">Conditions:</span>
                              <div className="mt-1 space-y-1">
                                {term.conditions.map((condition, idx) => (
                                  <div key={idx} className="flex items-center gap-2">
                                    <PhosphorIcons.CaretRight size={16} className="text-primary"/>
                                    <span>{condition}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Risks Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-error/10">
                  <PhosphorIcons.Warning size={20} weight="bold" className="text-error"/>
                </div>
                <h3 className="card-title text-lg">Contract Risks</h3>
              </div>

              <div className="space-y-3">
                {Array.isArray(result?.contract_analysis?.risks) && result.contract_analysis.risks.map((risk, index) => (
                  <div key={index} className="p-3 bg-base-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium capitalize">{risk.type.replace(/_/g, ' ')}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getSeverityColor(risk.severity)}`}>
                        {risk.severity}
                      </span>
                    </div>
                    <div className="text-sm">{risk.description}</div>
                    {Array.isArray(risk?.impact_areas) && risk.impact_areas.length > 0 && (
                      <div className="mt-2">
                        <span className="text-sm text-base-content/70">Impact Areas:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {risk.impact_areas.map((area, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-base-300 rounded-full text-xs capitalize">
                              {area}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {Array.isArray(risk?.mitigation_suggestions) && risk.mitigation_suggestions.length > 0 && (
                      <div className="mt-2">
                        <span className="text-sm text-base-content/70">Mitigation Suggestions:</span>
                        <div className="mt-1 space-y-1">
                          {risk.mitigation_suggestions.map((suggestion, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm">
                              <PhosphorIcons.CaretRight size={16} className="text-primary"/>
                              <span>{suggestion}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Compliance Requirements */}
          {result?.contract_analysis?.compliance_requirements && (
            <div className="card bg-base-100 shadow-sm">
              <div className="card-body p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-2 rounded-lg bg-info/10">
                    <PhosphorIcons.Shield size={20} weight="bold" className="text-info"/>
                  </div>
                  <h3 className="card-title text-lg">Compliance Requirements</h3>
                </div>

                <div className="space-y-3">
                  {Array.isArray(result?.contract_analysis?.compliance_requirements) && result.contract_analysis.compliance_requirements.map((req, index) => (
                    <div key={index} className="p-3 bg-base-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="font-medium capitalize">{req.type.replace(/_/g, ' ')}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          req.status === 'compliant' ? 'bg-success/20 text-success' :
                          req.status === 'non_compliant' ? 'bg-error/20 text-error' :
                          'bg-warning/20 text-warning'
                        }`}>
                          {req.status.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <div className="text-sm mt-1">{req.description}</div>
                      {req.deadline && (
                        <div className="text-sm mt-1">
                          <span className="text-base-content/70">Deadline: </span>
                          {new Date(req.deadline).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Analysis Summary Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <PhosphorIcons.ChartBar size={20} weight="bold" className="text-primary"/>
                </div>
                <h3 className="card-title text-lg">Analysis Summary</h3>
              </div>

              <div className="space-y-3">
                {/* Analysis Quality */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Analysis Quality</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success capitalize">
                      {result?.metadata?.analysis_quality}
                    </span>
                  </div>
                </div>

                {/* Confidence Score */}
                {result?.metadata?.confidence_score != null && (
                  <div className="p-3 bg-base-200 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium">Confidence</span>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
                        {Math.round(result.metadata.confidence_score * 100)}%
                      </span>
                    </div>
                    <div className="w-full bg-base-300 rounded-full h-2">
                      <div 
                        className="h-2 rounded-full bg-success"
                        style={{width: `${result.metadata.confidence_score * 100}%`}}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Document Statistics */}
                {result?.metadata?.document_statistics && (
                  <div className="p-3 bg-base-200 rounded-lg">
                    <span className="font-medium">Document Statistics</span>
                    <div className="mt-2 space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-base-content/70">Total Pages:</span>
                        <span>{result.metadata.document_statistics.total_pages}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-base-content/70">Total Clauses:</span>
                        <span>{result.metadata.document_statistics.total_clauses}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-base-content/70">Clarity Score:</span>
                        <span>{Math.round(result.metadata.document_statistics.language_clarity_score * 100)}%</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Additional Metadata */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="space-y-2 text-sm">
                    {result?.metadata?.contract_type && (
                      <div className="flex justify-between">
                        <span className="text-base-content/70">Contract Type:</span>
                        <span className="capitalize">{result.metadata.contract_type.replace(/_/g, ' ')}</span>
                      </div>
                    )}
                    {result?.metadata?.governing_law && (
                      <div className="flex justify-between">
                        <span className="text-base-content/70">Governing Law:</span>
                        <span>{result.metadata.governing_law}</span>
                      </div>
                    )}
                    {result?.metadata?.jurisdiction && (
                      <div className="flex justify-between">
                        <span className="text-base-content/70">Jurisdiction:</span>
                        <span>{result.metadata.jurisdiction}</span>
                      </div>
                    )}
                    {result?.metadata?.analysis_timestamp && (
                      <div className="flex justify-between">
                        <span className="text-base-content/70">Analysis Date:</span>
                        <span>{new Date(result.metadata.analysis_timestamp).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractAnalysisViewResult